import {useCallback, useEffect, useMemo, useState} from 'react'
import {Coins, Cpu, Pencil, ShieldCheck, UserCog, Users} from 'lucide-react'
import {PageHeader} from '../components/PageHeader'
import {Card} from '../components/ui/Card'
import {Badge} from '../components/ui/Badge'
import {Button} from '../components/ui/Button'
import {EmptyState, Skeleton} from '../components/ui/States'
import {Modal} from '../components/ui/Modal'
import {Field, Input, Select} from '../components/ui/Field'
import {ModelAllowlist} from '../components/ModelAllowlist'
import {accessUsersService} from '../services/accessUsersService'
import {budgetService} from '../services/budgetService'
import {modelsService} from '../services/modelsService'
import {ApiError} from '../services/apiClient'
import {useModelsStore} from '../store/modelsStore'
import {toast} from '../store/uiStore'
import {formatBRL} from '../lib/utils'
import type {Area, StudioAccessUser} from '../types'

type UserForm = StudioAccessUser & {areaId: string}
const MAX_VISIBLE_USERS = 30

export function UsersPage() {
  const [users, setUsers] = useState<StudioAccessUser[]>([])
  const [areas, setAreas] = useState<Area[]>([])
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<UserForm | null>(null)
  const [availableModelIds, setAvailableModelIds] = useState<string[]>([])
  const catalog = useModelsStore((state) => state.catalog)
  const ensureModels = useModelsStore((state) => state.load)
  const reloadAllowed = useModelsStore((state) => state.reloadAllowed)

  const load = useCallback(async () => {
    try {
      const [authorizedUsers, allAreas] = await Promise.all([
        accessUsersService.list(),
        budgetService.listAreas(),
      ])
      setUsers(authorizedUsers)
      setAreas(allAreas)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Erro ao carregar usuários.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    ensureModels()
  }, [ensureModels, load])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedQuery(query), 300)

    return () => window.clearTimeout(timeoutId)
  }, [query])

  const selectedAreaId = form?.areaId
  useEffect(() => {
    if (!selectedAreaId) {
      setAvailableModelIds([])
      return
    }
    modelsService
      .getAreaModels(selectedAreaId)
      .then((ids) => {
        setAvailableModelIds(ids)
        setForm((current) =>
          current && current.areaId === selectedAreaId
            ? {...current, modelIds: current.modelIds.filter((id) => ids.includes(id))}
            : current
        )
      })
      .catch(() => setAvailableModelIds([]))
  }, [selectedAreaId])

  const areaNamesById = useMemo(() => new Map(areas.map((area) => [area.id, area.name])), [areas])
  const searchableUsers = useMemo(
    () =>
      users.map((user) => ({
        user,
        searchText: [user.name, user.email, user.role, areaNamesById.get(user.areaId || '')]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase('pt-BR'),
      })),
    [areaNamesById, users]
  )
  const {visibleUsers, hasMoreResults} = useMemo(() => {
    const normalized = debouncedQuery.trim().toLocaleLowerCase('pt-BR')
    const matches: StudioAccessUser[] = []

    for (const entry of searchableUsers) {
      if (normalized && !entry.searchText.includes(normalized)) continue

      matches.push(entry.user)
      if (matches.length > MAX_VISIBLE_USERS) break
    }

    return {
      visibleUsers: matches.slice(0, MAX_VISIBLE_USERS),
      hasMoreResults: matches.length > MAX_VISIBLE_USERS,
    }
  }, [debouncedQuery, searchableUsers])

  const configuredCount = users.filter((user) => !!user.personId && !!user.areaId).length

  function editUser(user: StudioAccessUser) {
    setForm({
      ...user,
      areaId: user.areaId || areas[0]?.id || '',
      role: user.role || '',
      budgetBRL: user.budgetBRL || 0,
      topupBalance: user.topupBalance || 0,
      modelIds: user.modelIds || [],
    })
  }

  async function saveUser() {
    if (!form?.areaId) return
    setSaving(true)
    try {
      await accessUsersService.update(form.guid, {
        areaId: form.areaId,
        role: form.role,
        budgetBRL: form.budgetBRL,
        topupBalance: form.topupBalance,
        modelIds: form.modelIds,
      })
      setForm(null)
      toast.success('Configurações do usuário salvas.')
      await load()
      reloadAllowed()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Erro ao salvar usuário.')
    } finally {
      setSaving(false)
    }
  }

  const selectedArea = areas.find((area) => area.id === form?.areaId)
  const allocatedToOthers = form
    ? users
        .filter((user) => user.areaId === form.areaId && user.guid !== form.guid)
        .reduce((total, user) => total + user.budgetBRL, 0)
    : 0
  const availableInArea = Math.max(0, (selectedArea?.budgetBRL || 0) - allocatedToOthers)

  return (
    <>
      <PageHeader
        title='Usuários'
        subtitle={`${users.length} com acesso ao Agent Studio · ${configuredCount} configurado(s)`}
        search={query}
        onSearch={setQuery}
        searchPlaceholder='Buscar usuário...'
      />

      <div className='flex-1 overflow-y-auto p-8 scrollbar-thin'>
        {loading ? (
          <div className='space-y-3'>
            {Array.from({length: 5}).map((_, index) => (
              <Skeleton key={index} className='h-20 w-full' />
            ))}
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title='Nenhum usuário autorizado'
            description='Conceda a permissão de acesso ao Agent Studio no Sistema Interno para o usuário aparecer aqui.'
          />
        ) : (
          <Card className='overflow-hidden'>
            <div className='hidden grid-cols-[minmax(240px,1.5fr)_minmax(150px,1fr)_130px_120px_120px_48px] gap-4 border-b border-line bg-cloud/60 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate lg:grid'>
              <span>Usuário</span>
              <span>Área</span>
              <span>Budget mensal</span>
              <span>Saldo extra</span>
              <span>Modelos</span>
              <span />
            </div>
            <div className='divide-y divide-line'>
              {visibleUsers.map((user) => {
                const area = areas.find((item) => item.id === user.areaId)
                const initials = user.name
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0]?.toUpperCase())
                  .join('')
                return (
                  <div
                    key={user.guid}
                    className='grid gap-3 px-5 py-4 lg:grid-cols-[minmax(240px,1.5fr)_minmax(150px,1fr)_130px_120px_120px_48px] lg:items-center lg:gap-4'
                  >
                    <div className='flex min-w-0 items-center gap-3'>
                      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy/10 text-xs font-semibold text-navy'>
                        {initials || 'U'}
                      </div>
                      <div className='min-w-0'>
                        <div className='flex flex-wrap items-center gap-2'>
                          <p className='truncate text-sm font-semibold text-ink'>{user.name}</p>
                          <Badge tone={user.personId ? 'success' : 'warning'} dot>
                            {user.personId ? 'Configurado' : 'Pendente'}
                          </Badge>
                        </div>
                        <p className='truncate text-xs text-slate'>{user.email}</p>
                        {user.role && <p className='truncate text-xs text-slate/80'>{user.role}</p>}
                      </div>
                    </div>
                    <div className='flex items-center gap-2 text-sm text-ink'>
                      <ShieldCheck className='h-4 w-4 text-slate' />
                      {area?.name || 'Sem área definida'}
                    </div>
                    <span className='text-sm font-medium text-ink'>
                      {formatBRL(user.budgetBRL)}
                    </span>
                    <span className='flex items-center gap-1.5 text-sm text-ink'>
                      <Coins className='h-4 w-4 text-brand-orange' /> {formatBRL(user.topupBalance)}
                    </span>
                    <span className='flex items-center gap-1.5 text-sm text-ink'>
                      <Cpu className='h-4 w-4 text-slate' /> {user.modelIds.length}
                    </span>
                    <Button
                      size='icon'
                      variant='ghost'
                      onClick={() => editUser(user)}
                      aria-label={`Configurar ${user.name}`}
                      title='Configurar usuário'
                    >
                      <Pencil className='h-4 w-4' />
                    </Button>
                  </div>
                )
              })}
            </div>
            {visibleUsers.length === 0 && (
              <p className='px-5 py-10 text-center text-sm text-slate'>
                Nenhum usuário encontrado.
              </p>
            )}
            {hasMoreResults && (
              <p className='border-t border-line px-5 py-3 text-center text-xs text-slate'>
                Exibindo os primeiros {MAX_VISIBLE_USERS} resultados. Continue digitando para
                refinar a busca.
              </p>
            )}
          </Card>
        )}
      </div>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title='Configurar usuário'
        width='max-w-2xl'
        footer={
          <>
            <Button variant='ghost' onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button
              variant='primary'
              icon={UserCog}
              loading={saving}
              disabled={!form?.areaId}
              onClick={saveUser}
            >
              Salvar configurações
            </Button>
          </>
        }
      >
        {form && (
          <>
            <div className='mb-4 rounded-xl bg-cloud px-4 py-3'>
              <p className='text-sm font-semibold text-ink'>{form.name}</p>
              <p className='text-xs text-slate'>{form.email}</p>
              <p className='mt-1 text-[11px] text-slate/80'>
                A identidade vem da permissão de acesso no Sistema Interno.
              </p>
            </div>
            {areas.length === 0 ? (
              <p className='rounded-xl border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-warning'>
                Crie uma área antes de configurar budgets e modelos para este usuário.
              </p>
            ) : (
              <>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <Field label='Área' className='mb-0'>
                    <Select
                      value={form.areaId}
                      onChange={(event) =>
                        setForm({...form, areaId: event.target.value, modelIds: []})
                      }
                    >
                      {areas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {area.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label='Cargo / função' className='mb-0'>
                    <Input
                      value={form.role || ''}
                      onChange={(event) => setForm({...form, role: event.target.value})}
                      placeholder='ex.: Analista de Operações'
                    />
                  </Field>
                  <Field label='Budget mensal (US$)' hint='renova todo mês' className='mb-0'>
                    <Input
                      type='number'
                      min={0}
                      step='1'
                      value={form.budgetBRL}
                      onChange={(event) =>
                        setForm({...form, budgetBRL: Math.max(0, Number(event.target.value))})
                      }
                    />
                  </Field>
                  <Field label='Saldo extra (US$)' hint='não reseta no mês' className='mb-0'>
                    <Input
                      type='number'
                      min={0}
                      step='1'
                      value={form.topupBalance}
                      onChange={(event) =>
                        setForm({...form, topupBalance: Math.max(0, Number(event.target.value))})
                      }
                    />
                  </Field>
                </div>
                <div className='mt-3 flex items-center gap-2 rounded-lg bg-cloud px-3 py-2 text-xs text-slate'>
                  <Users className='h-3.5 w-3.5' />
                  Disponível na área <strong className='text-ink'>
                    {selectedArea?.name}
                  </strong>: {formatBRL(availableInArea)} de{' '}
                  {formatBRL(selectedArea?.budgetBRL || 0)}
                </div>
                <ModelAllowlist
                  label='Modelos liberados para o usuário'
                  hint='limitado aos modelos permitidos para a área selecionada'
                  models={catalog.filter((model) => availableModelIds.includes(model.id))}
                  selected={form.modelIds}
                  onChange={(modelIds) => setForm({...form, modelIds})}
                  emptyHint='A área não possui modelos liberados. Configure os modelos da área primeiro.'
                />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  )
}
